import { CoreBox } from '@youscience/core';
import { MeWithAccessRecordsApiResponse } from '@youscience/bpa-common';
import { useRef } from 'react';

import HeadSection from '@components/HeadSection';
import SidebarMenu from '@components/SidebarMenu';

import { useIsLoadingContent } from '@customHooks/useIsLoadingContent';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';

import { TenantSettings } from '@interfaces';

import { getUserPreferredName } from '@utils/helper';

import BriefcaseIcon from '@images/briefcase.svg?react';
import CvIcon from '@images/cv.svg?react';
import GraduateCapIcon from '@images/graduate-cap.svg?react';
import NotebookIcon from '@images/notebook.svg?react';
import PencilIcon from '@images/pencil.svg?react';

import AptitudesSection from '../components/HighSchool/AptitudesSection';
import CareerSection from '../components/HighSchool/CareerSection';
import EducationSection from '../components/HighSchool/EducationSection';

import { StyledMainSectionContentWrap, StyledMainSectionInner, sxStyles } from '../Landing.styles';

interface HighSchoolLandingPageProp {
  userData: MeWithAccessRecordsApiResponse;
  tenantSettings?: TenantSettings;
  userWithResultsReady: boolean;
}

export const HighSchoolLandingPage = ({
  userData,
  tenantSettings,
  userWithResultsReady,
}: HighSchoolLandingPageProp) => {
  const aptitudesRef = useRef(null);
  const careersRef = useRef(null);
  const resumeBuilderRef = useRef(null);
  const educationRef = useRef(null);
  const coursePlannerRef = useRef(null);
  const collegePlannerRef = useRef(null);

  const { isLoadingContent, addLoadingContent } = useIsLoadingContent();

  const menuItems = [
    {
      id: 1,
      title: 'Aptitudes',
      icon: <PencilIcon />,
      value: 'aptitudes',
      ref: aptitudesRef,
    },
    {
      id: 2,
      title: 'Careers',
      icon: <BriefcaseIcon />,
      value: 'careers',
      ref: careersRef,
    },
    {
      id: 3,
      title: 'Resume builder',
      icon: <CvIcon />,
      value: 'resume-builder',
      ref: resumeBuilderRef,
    },
    {
      id: 4,
      title: 'Education',
      icon: <GraduateCapIcon />,
      value: 'education',
      ref: educationRef,
    },
    {
      id: 5,
      title: 'High school course planner',
      icon: <NotebookIcon />,
      value: 'course-planner',
      ref: coursePlannerRef,
    },
    {
      id: 6,
      title: 'College planner',
      icon: <GraduateCapIcon />,
      value: 'college-planner',
      ref: collegePlannerRef,
    },
  ];

  const title = `${getUserPreferredName(userData)}, You're on an exciting journey!`;
  const subtitle =
    'Take a moment to consider your aptitudes and how they can help you make important education and career decisions. ' +
    'Use these resources to think, plan, and showcase your unique talents!';

  if (!tenantSettings) return null;

  return (
    <>
      <HeadSection title={title} subtitle={subtitle} breadcrumbsCurrentPage={BREADCRUMBS_PAGE_TEXT.LANDING} />

      <CoreBox sx={sxStyles.mainSectionBg}>
        <StyledMainSectionInner>
          <SidebarMenu menuItems={menuItems} tenantSettings={tenantSettings} isLoadingContent={isLoadingContent} />

          <StyledMainSectionContentWrap>
            <CoreBox ref={aptitudesRef}>
              <AptitudesSection userWithResultsReady={userWithResultsReady} onLoad={addLoadingContent('apitudes')} />
            </CoreBox>

            <CoreBox ref={careersRef}>
              <CareerSection
                isResumeBuilderEnabled={tenantSettings.resumeBuilderEnabled}
                userWithResultsReady={userWithResultsReady}
                resumeBuilderRef={resumeBuilderRef}
                onLoad={addLoadingContent('careers')}
              />
            </CoreBox>

            <CoreBox ref={educationRef}>
              <EducationSection
                isCoursePlannerEnabled={tenantSettings?.coursePlannerEnabled}
                coursePlannerRef={coursePlannerRef}
                collegePlannerRef={collegePlannerRef}
                userWithResultsReady={userWithResultsReady}
                onLoad={addLoadingContent('education')}
              />
            </CoreBox>
          </StyledMainSectionContentWrap>
        </StyledMainSectionInner>
      </CoreBox>
    </>
  );
};
